import { useEffect, useRef, useState } from "react";
import { Upload, Loader2, Save, Smartphone, FileImage } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { adminApi } from "@/lib/api";
import { useBranding } from "@/contexts/BrandingContext";
import { toImageDataUrl } from "@/lib/image";

// Convert file → base64 (no prefix)
async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      const base64 = dataUrl.replace(/^data:[^;]+;base64,/, "");
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function AdminIcons() {
  const branding = useBranding();
  const inputRef = useRef(null);
  const pwaInputRef = useRef(null);
  const [state, setState] = useState({
    icon_base64: null,
    pwa_icon_base64: null,
    company_name: "",
    company_tagline: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi
      .icons()
      .then((data) => {
        setState({
          icon_base64: typeof data.icon_base64 === "string" && data.icon_base64.length > 10 ? data.icon_base64 : null,
          pwa_icon_base64: typeof data.pwa_icon_base64 === "string" && data.pwa_icon_base64.length > 10 ? data.pwa_icon_base64 : null,
          company_name: data.company_name || "",
          company_tagline: data.company_tagline || "",
        });
      })
      .finally(() => setLoading(false));
  }, []);

  const handleFile = async (f, field = "icon_base64") => {
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 2 Mo)");
      return;
    }
    try {
      const b64 = await fileToBase64(f);
      setState((s) => ({ ...s, [field]: b64 }));
    } catch {
      toast.error("Lecture impossible");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateIcons(state);
      toast.success("Logo & marque mis à jour");
      await branding.refresh();
    } catch {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-[var(--vidoc-text-secondary)]">Chargement...</div>;

  return (
    <div data-testid="admin-icons-page">
      <PageHeader title="Logo & marque" subtitle="Visible sur les PDF et l'interface" />

      <div className="px-4 md:px-8 py-6 max-w-2xl space-y-6">
        {/* ── Logo de l'application (Login, PDFs, etc.) ─────────────── */}
        <section className="bg-white rounded-xl border border-[var(--vidoc-border)] p-5 space-y-4">
          <div className="flex items-center gap-2">
            <FileImage className="w-4 h-4 text-[var(--vidoc-primary)]" />
            <h2 className="text-sm font-semibold">Logo de l'application</h2>
          </div>
          <p className="text-xs text-[var(--vidoc-text-secondary)] -mt-2">
            Affiché en haut de l'écran de connexion et en en-tête de tous les PDFs envoyés aux clients.
          </p>
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 rounded-2xl bg-[var(--vidoc-bg-secondary)] flex items-center justify-center overflow-hidden">
              {state.icon_base64 ? (
                <img
                  src={toImageDataUrl(state.icon_base64)}
                  alt="Logo"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="text-[var(--vidoc-text-tertiary)] text-xs text-center">
                  Aucun logo
                </div>
              )}
            </div>
            <div className="flex-1 space-y-2">
              <button
                data-testid="logo-upload-btn"
                onClick={() => inputRef.current?.click()}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--vidoc-bg-secondary)] hover:bg-[var(--vidoc-bg-tertiary)] text-sm font-medium touch-target"
              >
                <Upload className="w-4 h-4" />
                Choisir un fichier
              </button>
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0], "icon_base64")}
              />
              <p className="text-xs text-[var(--vidoc-text-tertiary)]">
                Format PNG/JPG, fond blanc / carré recommandé. Max 2 Mo.
              </p>
            </div>
          </div>
        </section>

        {/* ── Icône iPhone / écran d'accueil PWA ─────────────────────── */}
        <section className="bg-white rounded-xl border border-[var(--vidoc-border)] p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-[var(--vidoc-primary)]" />
            <h2 className="text-sm font-semibold">Icône bouton iPhone / Android</h2>
          </div>
          <p className="text-xs text-[var(--vidoc-text-secondary)] -mt-2">
            Affichée <strong>uniquement</strong> sur l'écran d'accueil du téléphone quand l'app est installée comme PWA. Préférer un carré coloré avec un texte court, lisible à 60×60 px.
          </p>
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 rounded-2xl bg-[var(--vidoc-bg-secondary)] flex items-center justify-center overflow-hidden">
              {state.pwa_icon_base64 ? (
                <img
                  src={toImageDataUrl(state.pwa_icon_base64)}
                  alt="Icône PWA"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="text-[var(--vidoc-text-tertiary)] text-xs text-center">
                  Aucune icône
                </div>
              )}
            </div>
            <div className="flex-1 space-y-2">
              <button
                data-testid="pwa-icon-upload-btn"
                onClick={() => pwaInputRef.current?.click()}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--vidoc-bg-secondary)] hover:bg-[var(--vidoc-bg-tertiary)] text-sm font-medium touch-target"
              >
                <Upload className="w-4 h-4" />
                Choisir un fichier
              </button>
              <input
                ref={pwaInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0], "pwa_icon_base64")}
              />
              <p className="text-xs text-[var(--vidoc-text-tertiary)]">
                PNG carré 1024×1024 idéal. Si vide, l'icône Logo ci-dessus est utilisée.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-xl border border-[var(--vidoc-border)] p-5 space-y-3">
          <h2 className="text-sm font-semibold">Identité</h2>
          <label className="block">
            <div className="text-xs font-medium text-[var(--vidoc-text-secondary)] mb-1">
              Nom de la maison de ventes
            </div>
            <input
              data-testid="company-name-input"
              value={state.company_name}
              onChange={(e) => setState((s) => ({ ...s, company_name: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-lg bg-[var(--vidoc-bg-secondary)] outline-none focus:bg-white focus:ring-2 ring-[var(--vidoc-primary)]"
            />
          </label>
          <label className="block">
            <div className="text-xs font-medium text-[var(--vidoc-text-secondary)] mb-1">
              Sous-titre / signature
            </div>
            <input
              data-testid="company-tagline-input"
              value={state.company_tagline}
              onChange={(e) => setState((s) => ({ ...s, company_tagline: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-lg bg-[var(--vidoc-bg-secondary)] outline-none focus:bg-white focus:ring-2 ring-[var(--vidoc-primary)]"
            />
          </label>
        </section>

        <button
          data-testid="branding-save-btn"
          onClick={handleSave}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[var(--vidoc-primary)] text-white font-semibold touch-target hover:bg-[var(--vidoc-primary-dark)] disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}
